import _ from "lodash";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useMutation, useQueryCache } from "react-query";
import { useMedia } from "react-media";
import { useParams, useLocation, Redirect, useHistory, Switch, Route } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import moment from "moment";
import queryString from "query-string";
import { postPermitContactAcceptInviteAndConnect } from "../../../../queries/permits";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import { Icon, Header, Segment, Menu, Grid, Button } from "semantic-ui-react";
import identitySelectors from "../../../../selectors/identity";
import { CircularIcon } from "../../components/CircularIcon";
import { CitizenInspectionList } from "../../components/inspections/CitizenInspectionList";
import { CommunicationsCard } from "../../components/CommunicationsCard";
import PermitsPayment from "../../../../features/permits/components/Payment";
import PublicApplicationDetails from "../../../../features/permits/components/PublicApplicationDetails";
import PermitsForm from "../../../../features/permits/components/Form";
import { PastPayments } from "./PastPayments";
import { CurrentBalance } from "./CurrentBalance";
import { CitizenContactsTab } from "./CitizenContactsTab";
import PlaceholderUntilLoaded from "../../../../components/global_components/PlaceholderUntilLoaded";
import DetailsHeader from "../../components/public/DetailsHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import usePermitContacts from "../../hooks/usePermitContacts";
import { SemanticICONS, SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import {
  faClipboardListCheck,
  faComments,
  faDollarSign,
  faDownload,
  faGrid,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import "./PermitDetails.scss";
import { getDocumentDownload } from "../../../../queries/documents";
import { AccessibleVerticalTabs } from "../../../../components/global_components/AccessibleVerticalTabs";
import usePublicInspections from "../../hooks/inspections/public/usePublicInspections";
import usePermit from "../../hooks/permits/usePermit";
import usePermitType from "../../hooks/permits/usePermitType";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";

export const PermitDetails = () => {
  const location = useLocation();
  const contentRef = useRef(null);
  const isLoggedIn = identitySelectors.isLoggedIn();
  const { site, id } = useParams<{ site: string; id: string }>();
  const permitForm = useForm({
    shouldUnregister: false,
  });
  const history = useHistory();
  const networkHeaders = useNetworkHeaders();
  const permitQuery = usePermit({ permitId: id });
  const queryCache = useQueryCache();
  const { token, contact_id } = useMemo(() => queryString.parse(location.search), [location.search]);
  const [validateToken, tokenValidateState] = useMutation(
    postPermitContactAcceptInviteAndConnect(
      networkHeaders,
      id,
      contact_id as string, //typescript throws error if cast isn't here despite boolean checking for it
    ),
    {
      onSuccess: () => {
        queryCache.invalidateQueries(["permits", id.toString()]);
      },
    },
  );
  const { data: inspectionData } = usePublicInspections({ recordId: id });
  const inspectionList = useMemo(() => _.get(inspectionData, "data"), [inspectionData]);
  const permitsTypeId = useMemo(() => _.get(permitQuery.data, "data.attributes.permits-type-id"), [permitQuery.data]);
  const permitsDocuments = useMemo(() => _.get(permitQuery.data, "data.attributes.documents.data"), [permitQuery.data]);
  const permitsTypeQuery = usePermitType({ permitTypeId: permitsTypeId });
  const permitsTypeName = useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.name"), [permitsTypeQuery.data]);
  const status = useMemo(() => _.get(permitQuery.data, "data.attributes.status"), [permitQuery.data]);
  const submittedDate = useMemo(() => _.get(permitQuery.data, "data.attributes.created-at"), [permitQuery.data]);
  const expirationDate = useMemo(() => _.get(permitQuery.data, "data.attributes.expired-at"), [permitQuery.data]);
  const confirmationNumber = useMemo(
    () => _.get(permitQuery.data, "data.attributes.confirmation-number"),
    [permitQuery.data],
  );
  const formattedConfirmationNumber = (confirmationNumber || "").match(/.{3}/g)?.join("-");
  const formalNumber = useMemo(() => _.get(permitQuery.data, "data.attributes.reservation-number"), [permitQuery.data]);
  const totalPaid = useMemo(() => _.get(permitQuery.data, "data.attributes.total-paid-in-cents"), [permitQuery.data]);
  const activeFees = useMemo(
    () => _.get(permitQuery.data, "data.attributes.total-active-fee-in-cents"),
    [permitQuery.data],
  );
  const feeList = useMemo(() => _.get(permitQuery.data, "data.attributes.fee-details"), [permitQuery.data]);
  const transactions = useMemo(() => _.get(permitQuery.data, "data.attributes.transactions"), [permitQuery.data]);
  const balance = useMemo(() => _.get(permitQuery.data, "data.attributes.balance-due"), [permitQuery.data]);
  const paymentRequestedAt = useMemo(
    () => _.get(permitQuery.data, "data.attributes.payment-requested-at"),
    [permitQuery.data],
  );
  const { contacts, contactCustomFields, permitsFormFields } = usePermitContacts({ permitId: id });
  const permitDoesNotExist = useMemo(() => _.get(permitQuery, "error.status") === 404, [permitQuery]);
  const userCannotViewPermit = useMemo(() => _.get(permitQuery, "error.status") === 403, [permitQuery]);
  const userCanEditPermit = useMemo(
    () => _.get(permitQuery.data, "data.attributes.user-can-edit-permit"),
    [permitQuery.data],
  );

  const isSmallScreen = useMedia({ query: "(max-width: 640px)" });
  const isMediumScreen = useMedia({ query: "(max-width: 991px)" });
  const [workflows, setWorkflows] = useState<any[]>([]);

  const downloadDocument = async (documentId: string) => {
    const { s3SignedUrl } = await getDocumentDownload(networkHeaders)(documentId);
    window.open(s3SignedUrl, "_blank");
  };

  useEffect(() => {
    if (!formattedConfirmationNumber || !permitsTypeName) {
      document.title = "Application Details";
      return;
    }
    document.title = `${formattedConfirmationNumber} | ${permitsTypeName}`;
  }, [formattedConfirmationNumber, permitsTypeName]);

  //redirect to login and redirect back to page once logged in or registered
  if (!isLoggedIn) {
    return (
      <Redirect
        to={`/${site}/public/login?redirect=/${site}/public/permits/details/${id}${encodeURIComponent(
          window.location.search,
        )}`}
      />
    );
  }

  //validate that we have a valid query string from email if it exists
  const isQueryValid = token && contact_id && typeof token === "string" && typeof contact_id === "string";

  //only call validate token if user doesn't have permission, we have a valid query, and we haven't already attempted to validate the token
  if (userCannotViewPermit && isQueryValid && tokenValidateState.isIdle) {
    validateToken({ data: { attributes: { token: token as string } } }); //typescript throws error if cast isn't here despite boolean checking for it
  }

  const isLoading = permitQuery.isLoading || tokenValidateState.isLoading;

  const menuItems = [
    {
      label: "Application",
      name: "application",
      icon: <FontAwesomeIcon size='xl' icon={faGrid} />,
      visible: true,
      index: 0,
    },
    {
      label: "Fees & Payments",
      name: "fees",
      icon: <FontAwesomeIcon size='xl' icon={faDollarSign} />,
      visible: true,
      index: 1,
    },
    {
      label: "Inspections",
      name: "inspections",
      icon: <FontAwesomeIcon size='xl' icon={faClipboardListCheck} />,
      visible: inspectionList === undefined ? false : inspectionList.length > 0,
      index: 2,
    },
    {
      label: "Communication",
      name: "communication",
      icon: <FontAwesomeIcon size='xl' icon={faComments} />,
      visible: true,
      index: 3,
    },
    {
      label: "Contacts",
      name: "contacts",
      icon: <FontAwesomeIcon size='xl' icon={faUsers} />,
      visible: contacts.length > 0,
      index: 4,
    },
  ];

  useEffect(() => {
    const attributes = { ...permitQuery.data?.data?.attributes };
    let tempWorkflows = attributes?.steps?.map((el: any) => {
      return {
        id: el.data.id,
        type: el.data.type,
        ...el.data.attributes,
      };
    });
    tempWorkflows?.sort(function (a: any, b: any) {
      return a.order - b.order;
    });
    tempWorkflows = tempWorkflows?.map((el: any) => ({ ...el, assignee_id: el["assignee-id"] }));
    setWorkflows(tempWorkflows ?? []);
  }, [permitQuery.data]);

  // returns information on error
  const getErrorInfo = (): { icon: SemanticICONS; text: string; color: SemanticCOLORS } | null => {
    if (permitDoesNotExist) {
      return {
        icon: "exclamation circle",
        text: "Sorry, we couldn't find this permit",
        color: "grey",
      };
    }
    if (userCannotViewPermit && !(tokenValidateState.isSuccess || tokenValidateState.isLoading)) {
      return {
        icon: "exclamation triangle",
        text: "Sorry, it looks like you do not have permission to view this application.",
        color: "red",
      };
    }
    return null;
  };

  // renders error screen if permit does not exist or user does not have permission to view permit
  const renderErrorScreen = () => {
    // display error, if there is one
    const errorInfo = getErrorInfo();
    if (errorInfo) {
      return (
        <div
          style={{
            marginTop: "200px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "30px",
            textAlign: "center",
          }}
        >
          <Icon name={errorInfo.icon} size='huge' color={errorInfo.color} />
          <Header size='huge'>{errorInfo.text}</Header>
          <Button href={`/${site}/public/permits/list`} color='blue' content='Return to Dashboard' size='big' />
        </div>
      );
    }

    if (userCannotViewPermit && tokenValidateState.isSuccess) return <p> Loading </p>; // permit is loading, show spinner

    return null;
  };

  const preventPermitRender = userCannotViewPermit || permitDoesNotExist; // prevents rendering permit if loading or error
  const handleTabClick = (hash: string) => {
    history.push(`/${site}/public/permits/details/${id}/${hash}`);
  };
  const renderDocumentsAndWorkflowCards = () => {
    return (
      <>
        <Segment className='public-portal-grid-segment'>
          <Header as='h2' className='menuHeader captionText'>
            DOCUMENTS
          </Header>
          <PlaceholderUntilLoaded isLoading={isLoading} lineCount={2}>
            {permitsDocuments?.length == 0 && <p className='secondaryText'> No documents have been issued </p>}
            {permitsDocuments?.length > 0 && (
              <Menu fluid secondary vertical>
                {permitsDocuments?.map((document: any, index: number) => {
                  return (
                    <Menu.Item
                      className='permitsMenu'
                      style={{ paddingLeft: 6 }}
                      name={document.attributes?.title}
                      key={index}
                    >
                      <Button
                        onClick={() => downloadDocument(document.id)}
                        className='no-padding-button tw-flex tw-items-center tw-gap-2 tw-font-bold tw-text-blue hover:tw-underline'
                      >
                        <FontAwesomeIcon className='hover:tw-cursor-pointer' icon={faDownload} />
                        <span>{document.attributes?.title}</span>
                      </Button>

                      <p className='menuHeader, secondaryText' style={{ marginTop: 8 }}>
                        Issued {moment(document.attributes.created_at).format("MMM DD, YYYY")}
                      </p>
                    </Menu.Item>
                  );
                })}
              </Menu>
            )}
          </PlaceholderUntilLoaded>
        </Segment>
        <Segment className='public-portal-grid-segment'>
          <Header as='h2' className='menuHeader captionText'>
            Timeline
          </Header>
          <PlaceholderUntilLoaded isLoading={isLoading} lineCount={2}>
            <ol className='tw-pl-0'>
              {workflows?.map((item: any, index: number) => {
                const { status, title, description } = item;
                const workflowHasBeenReviewed = status == "skipped" || status == "completed" || status == "rejected";
                return (
                  <li key={index} className='timeline-item'>
                    <div>
                      {workflowHasBeenReviewed ? (
                        <CircularIcon active={false} type={status} />
                      ) : (
                        <CircularIcon
                          persona='citizen'
                          displayIcon={index + 1}
                          active={status === "active"}
                          type='number'
                        />
                      )}
                    </div>
                    <div style={{ marginLeft: 8 }}>
                      {workflowHasBeenReviewed && (
                        <p className='secondaryText' style={{ marginBottom: 0 }}>
                          {moment(item["status-updated-at"]).format("MM / DD / YY")}
                        </p>
                      )}
                      {!description && <p className={status == "active" ? "active-title" : "normal-title"}>{title}</p>}
                      {description && (
                        <p className={status == "active" ? "active-title" : "normal-title"}>{description}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </PlaceholderUntilLoaded>
        </Segment>
      </>
    );
  };

  return (
    <PublicWrapper homeUrl={`/${site}/public/permits`} contentRef={contentRef}>
      <Segment basic style={{ flex: "1", marginTop: 16, display: "flex", flexDirection: "column" }}>
        {renderErrorScreen()}
        {!preventPermitRender && (
          <Grid className='public-portal-grid'>
            <Grid.Column
              width={16}
              style={{ paddingTop: "0", paddingBottom: "0" }}
              className='public-portal-grid-column'
            >
              <DetailsHeader
                permitsTypeName={permitsTypeName}
                formalNumber={formalNumber}
                formattedConfirmationNumber={formattedConfirmationNumber}
                status={status}
                isLoading={isLoading}
                isSmallScreen={isSmallScreen}
              />
            </Grid.Column>
            <Grid.Column computer={6} tablet={16} mobile={16} className='public-portal-grid-column'>
              <Segment className='dashboard-tabs'>
                <PlaceholderUntilLoaded isLoading={isLoading} lineCount={4}>
                  <AccessibleVerticalTabs
                    menuItems={menuItems}
                    handleTabClick={handleTabClick}
                    isMediumScreen={isMediumScreen}
                    url={`${site}/public/permits/details/${id}`}
                  />
                </PlaceholderUntilLoaded>
              </Segment>
              {!isMediumScreen && renderDocumentsAndWorkflowCards()}
            </Grid.Column>
            <Grid.Column
              computer={10}
              tablet={16}
              mobile={16}
              style={{ paddingTop: isMediumScreen ? 0 : 14 }}
              role='tabpanel'
              tabIndex={0}
              className='public-portal-grid-column'
            >
              <Switch>
                <Route
                  path='/:site/public/permits/details/:id/application'
                  render={() => (
                    <>
                      <PublicApplicationDetails
                        confirmationNumber={formattedConfirmationNumber}
                        submittedDate={submittedDate}
                        expirationDate={expirationDate}
                        isLoading={isLoading}
                      />
                      <Segment padded className='public-portal-grid-segment' attached='bottom'>
                        <FormProvider {...permitForm}>
                          <PermitsForm permitId={id} permitsTypeId={permitsTypeId} isPublicView={true} readonly />
                        </FormProvider>
                      </Segment>
                    </>
                  )}
                />
                <Route
                  path='/:site/public/permits/details/:id/communication'
                  render={() => (
                    <CommunicationsCard
                      site={site}
                      permitsTypeName={permitsTypeName}
                      permitNumber={confirmationNumber}
                      permitId={id}
                      isPublicView={true}
                    />
                  )}
                />
                <Route
                  path='/:site/public/permits/details/:id/fees'
                  render={() => (
                    <>
                      <CurrentBalance balance={balance / 100} hideBalance={paymentRequestedAt == null} />
                      <PermitsPayment
                        feeList={feeList}
                        paid={totalPaid}
                        activeFees={activeFees}
                        id={id}
                        details={true}
                        balance={balance}
                        paymentRequested={paymentRequestedAt != null}
                        subtotal={permitQuery.data?.data.attributes.subtotal}
                      />
                      {transactions?.length > 0 && (
                        <PastPayments
                          id={id}
                          paid={totalPaid / 100}
                          balance={balance / 100}
                          transactions={transactions}
                          hideBalance={paymentRequestedAt == null}
                        />
                      )}
                    </>
                  )}
                />
                <Route
                  path='/:site/public/permits/details/:id/contacts'
                  render={() => (
                    <CitizenContactsTab
                      contacts={contacts}
                      contactCustomFields={contactCustomFields}
                      formFields={permitsFormFields}
                      userCanEditPermit={userCanEditPermit}
                    />
                  )}
                />
                <Route
                  path='/:site/public/permits/details/:id/inspections'
                  render={({ match }) => {
                    if (inspectionList !== undefined && inspectionList.length > 0) {
                      return <CitizenInspectionList permitId={id} />;
                    } else {
                      return (
                        <Redirect to={`/${match.params.site}/public/permits/details/${match.params.id}/application`} />
                      );
                    }
                  }}
                />
                {/* Add a redirect to /applications */}
                <Route
                  path='/:site/public/permits/details/:id'
                  render={({ match }) => (
                    <Redirect
                      to={{
                        pathname: `/${match.params.site}/public/permits/details/${match.params.id}/application`,
                        search: location.search,
                      }}
                    />
                  )}
                />
              </Switch>
              {isMediumScreen && renderDocumentsAndWorkflowCards()}
            </Grid.Column>
          </Grid>
        )}
      </Segment>
    </PublicWrapper>
  );
};

export default PermitDetails;
